/**
 * Hero2Tab — CMS controls for the secondary hero section ("Hero2").
 *
 * Edits content.hero2 = { eyebrow, title, subtitle, bgImageUrl, bullets:[],
 *   speedMs, maxStack, holdMs }
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Upload, X, Plus, Trash2, GripVertical } from 'lucide-react';
import { HERO2_ICONS } from '../landing/Hero2Section';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  eyebrow: 'BUILT FOR MATCH DAY',
  title: 'From the touchline to the back of the net.',
  subtitle: 'Log every goal, card and sub the second it happens — and watch your stats, charts and player insights update in real time.',
  bgImageUrl: '',
  speedMs: 800,
  maxStack: 5,
  holdMs: 1500,
  bullets: [
    { id: 1, icon: 'badge-check', text: 'Match Stats Updated Live' },
    { id: 2, icon: 'flame', text: 'In-Form Player Tracker' },
    { id: 3, icon: 'clipboard-list', text: 'Tactics Board On Pitch' },
    { id: 4, icon: 'medal', text: 'Auto Player Highlights' },
    { id: 5, icon: 'heart', text: 'Welfare Tracking Built-In' },
  ],
};

export function Hero2Tab({ content, setContent }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const h = content.hero2 || {};

  const patch = (partial) => setContent(prev => ({
    ...prev,
    hero2: { ...DEFAULTS, ...(prev.hero2 || {}), ...partial },
  }));

  const bullets = Array.isArray(h.bullets) && h.bullets.length > 0 ? h.bullets : DEFAULTS.bullets;

  const updateBullet = (idx, field, value) => {
    const next = bullets.map((b, i) => i === idx ? { ...b, [field]: value } : b);
    patch({ bullets: next });
  };

  const addBullet = () => {
    const next = [...bullets, { id: Date.now(), icon: 'star', text: 'New checklist item' }];
    patch({ bullets: next });
  };

  const removeBullet = (idx) => {
    if (bullets.length <= 1) { toast.error('At least one bullet is required'); return; }
    patch({ bullets: bullets.filter((_, i) => i !== idx) });
  };

  const moveBullet = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= bullets.length) return;
    const next = [...bullets];
    [next[idx], next[j]] = [next[j], next[idx]];
    patch({ bullets: next });
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('section', 'hero2_bg');
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      patch({ bgImageUrl: res.data.image_url });
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const iconOptions = Object.keys(HERO2_ICONS);

  return (
    <Card data-testid="hero2-tab">
      <CardHeader>
        <CardTitle>Hero2 · Secondary Hero Section</CardTitle>
        <CardDescription>
          Image + auto-stacking "to-do" checklist. Each bullet stamps in with a green ✓,
          conveying how quickly tasks can be done on the touchline. Add this section
          via the <strong>Layout</strong> tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Copy */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Eyebrow pill</Label>
              <button
                type="button"
                onClick={() => patch({ show_eyebrow: !(h.show_eyebrow !== false) })}
                className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                  h.show_eyebrow !== false
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                data-testid="hero2-eyebrow-toggle"
              >
                {h.show_eyebrow !== false ? 'Visible' : 'Hidden'}
              </button>
            </div>
            <Input
              value={h.eyebrow ?? DEFAULTS.eyebrow}
              onChange={(e) => patch({ eyebrow: e.target.value })}
              placeholder="BUILT FOR MATCH DAY"
              className={`mt-1 ${h.show_eyebrow === false ? 'opacity-50' : ''}`}
              disabled={h.show_eyebrow === false}
              data-testid="hero2-eyebrow-input"
            />
            <p className="text-xs text-gray-500 mt-1">Small uppercase pill above the headline. Toggle the badge above to hide the pill entirely on the live page.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Title</Label>
              <button
                type="button"
                onClick={() => patch({ show_title: !(h.show_title !== false) })}
                className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                  h.show_title !== false
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                data-testid="hero2-title-toggle"
              >
                {h.show_title !== false ? 'Visible' : 'Hidden'}
              </button>
            </div>
            <Input
              value={h.title ?? DEFAULTS.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="From the touchline to the back of the net."
              className={`mt-1 ${h.show_title === false ? 'opacity-50' : ''}`}
              disabled={h.show_title === false}
              data-testid="hero2-title-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Subtitle</Label>
              <button
                type="button"
                onClick={() => patch({ show_subtitle: !(h.show_subtitle !== false) })}
                className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                  h.show_subtitle !== false
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                data-testid="hero2-subtitle-toggle"
              >
                {h.show_subtitle !== false ? 'Visible' : 'Hidden'}
              </button>
            </div>
            <Textarea
              value={h.subtitle ?? DEFAULTS.subtitle}
              onChange={(e) => patch({ subtitle: e.target.value })}
              placeholder="One-sentence description sits under the title."
              className={`mt-1 ${h.show_subtitle === false ? 'opacity-50' : ''}`}
              disabled={h.show_subtitle === false}
              rows={2}
              data-testid="hero2-subtitle-input"
            />
          </div>
        </div>

        {/* Background image */}
        <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm font-semibold">Background image</Label>
              <p className="text-xs text-gray-500 mt-0.5">Wide 16:10 image displayed inside the rounded card. Best with action / matchday photography.</p>
            </div>
            {h.bgImageUrl ? (
              <Button type="button" variant="outline" size="sm" onClick={() => patch({ bgImageUrl: '' })} data-testid="hero2-bg-remove">
                <X className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="hero2-bg-upload">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          </div>
          {h.bgImageUrl && (
            <div className="rounded-lg overflow-hidden border border-gray-200" style={{ aspectRatio: '16 / 10' }}>
              <img src={h.bgImageUrl} alt="Hero2 background" className="w-full h-full object-cover" data-testid="hero2-bg-preview" />
            </div>
          )}
        </div>

        {/* Animation settings */}
        <div className="pt-4 mt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-semibold">Pill arrival speed (ms)</Label>
            <Input
              type="number"
              min={400}
              max={3000}
              step={50}
              value={h.speedMs ?? DEFAULTS.speedMs}
              onChange={(e) => patch({ speedMs: Number(e.target.value) || DEFAULTS.speedMs })}
              className="mt-1"
              data-testid="hero2-speed-input"
            />
            <p className="text-xs text-gray-500 mt-1">Time between each pill appearing. 800ms is the sweet spot.</p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Max pills on screen</Label>
            <Input
              type="number"
              min={2}
              max={8}
              value={h.maxStack ?? DEFAULTS.maxStack}
              onChange={(e) => patch({ maxStack: Number(e.target.value) || DEFAULTS.maxStack })}
              className="mt-1"
              data-testid="hero2-maxstack-input"
            />
            <p className="text-xs text-gray-500 mt-1">How many to stack before cycling out and restarting.</p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Hold time (ms)</Label>
            <Input
              type="number"
              min={500}
              max={6000}
              step={100}
              value={h.holdMs ?? DEFAULTS.holdMs}
              onChange={(e) => patch({ holdMs: Number(e.target.value) || DEFAULTS.holdMs })}
              className="mt-1"
              data-testid="hero2-hold-input"
            />
            <p className="text-xs text-gray-500 mt-1">Pause after the stack fills before clearing.</p>
          </div>
        </div>

        {/* Bullets */}
        <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-semibold">Checklist bullets</Label>
              <p className="text-xs text-gray-500">Each bullet appears as a pill with an icon + the green ✓ check. Add as many as you like — only the first N (Max pills) are shown per cycle.</p>
            </div>
            <Button type="button" size="sm" onClick={addBullet} data-testid="hero2-add-bullet">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add bullet
            </Button>
          </div>
          <div className="space-y-2">
            {bullets.map((b, idx) => (
              <div key={b.id ?? idx} className="grid grid-cols-[auto_1fr_140px_auto] gap-2 items-center bg-gray-50 border border-gray-200 rounded-md p-2" data-testid={`hero2-bullet-row-${idx}`}>
                <div className="flex flex-col">
                  <button type="button" onClick={() => moveBullet(idx, -1)} className="p-0.5 text-gray-400 hover:text-gray-900 disabled:opacity-30" disabled={idx === 0} title="Move up">▲</button>
                  <button type="button" onClick={() => moveBullet(idx, 1)} className="p-0.5 text-gray-400 hover:text-gray-900 disabled:opacity-30" disabled={idx === bullets.length - 1} title="Move down">▼</button>
                </div>
                <Input
                  value={b.text}
                  onChange={(e) => updateBullet(idx, 'text', e.target.value)}
                  placeholder="Bullet text"
                  data-testid={`hero2-bullet-text-${idx}`}
                />
                <select
                  value={b.icon}
                  onChange={(e) => updateBullet(idx, 'icon', e.target.value)}
                  className="rounded-md border border-gray-200 bg-white px-2 py-2 text-sm"
                  data-testid={`hero2-bullet-icon-${idx}`}
                >
                  {iconOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeBullet(idx)} data-testid={`hero2-bullet-remove-${idx}`}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
